import { FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query'
import { SearchAssistantCriteriasMapper } from '@widgets/SearchAssistant/model'

import { transformResponseWithPagination } from '@helpers'
import { CriteriaType, URLS } from '@constants'
import {
  GETCriteriaData,
  GETCriteriaParams,
  GETCriteriaType,
  GETDefaultCriteriaParams,
  GETObjectWithDefaultCriteria,
  ModelWithPagination,
  POSTCriteriaParams,
  POSTCriteriaType,
  POSTDefaultCriteria,
  POSTVisibilityCriteriaType,
} from '@types'

import { commonAPI } from './common.api'

export const CriteriaApi = commonAPI.injectEndpoints({
  endpoints: build => ({
    createOrUpdateCriteria: build.mutation<GETCriteriaType, POSTCriteriaType>({
      query: criteria => ({
        url: URLS.FRONT_CFG + '/searchFilter',
        method: 'POST',
        body: criteria,
      }),
      invalidatesTags: ['Criterias'],
    }),
    addDefaultCriteria: build.mutation<void, POSTDefaultCriteria[]>({
      query: defaultCriterias => ({
        url: URLS.FRONT_CFG + '/searchFilter/default',
        method: 'POST',
        body: defaultCriterias,
      }),
      invalidatesTags: ['DefaultCriteria'],
    }),
    fetchDefaultCriteria: build.query<GETObjectWithDefaultCriteria[], GETDefaultCriteriaParams>({
      query: ({ searchAssistantId }) => ({
        url: URLS.FRONT_CFG + `/searchFilter/default/searchAssistant/id/${searchAssistantId}`,
      }),
      providesTags: ['DefaultCriteria'],
    }),
    // fetchCriterias: build.query<GETCriteriaData, GETCriteriaParams>({
    //   query: params => {
    //     const { objectCode, searchAssistantId } = params
    //
    //     return {
    //       url:
    //         URLS.FRONT_CFG +
    //         `/searchFilter/object/code/${objectCode}/searchAssistant/id/${searchAssistantId}`, //
    //     }
    //   },
    //   serializeQueryArgs: ({ endpointName, queryArgs }) => {
    //     return `${endpointName}:${queryArgs.objectCode}:${queryArgs.searchAssistantId}:${queryArgs.entityCode}:${queryArgs.instanceId}`
    //   },
    //   transformResponse: (output: GETCriteriaType[], meta: FetchBaseQueryMeta) => {
    //     return transformResponseWithPagination<GETCriteriaType[]>(output, meta)
    //   },
    //   providesTags: ['Criterias'],
    // }),
    // TODO(silanov): Remove this endpoint after migration to new API
    fetchAllCriterias: build.query<ModelWithPagination<CriteriaType[]>, GETCriteriaParams>({
      query: ({ objectCode, searchAssistantId }) => ({
        url:
          URLS.FRONT_CFG +
          `/searchFilter/object/code/${objectCode}/searchAssistant/id/${searchAssistantId}`,
      }),
      keepUnusedDataFor: 0,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}:${queryArgs.objectCode}:${queryArgs.searchAssistantId}:${queryArgs.instanceId}`
      },
      transformResponse: (response: GETCriteriaType[], meta: FetchBaseQueryMeta) => {
        const model = response.map(SearchAssistantCriteriasMapper.toModel)

        return transformResponseWithPagination<CriteriaType[]>(model, meta)
      },
      providesTags: ['Criterias'],
    }),
    fetchCriteriasList: build.query<GETCriteriaData, GETCriteriaParams>({
      query: ({ objectCode, sort, searchAssistantId }) => ({
        url: URLS.FRONT_CFG + `/searchFilter/searchAssistant/id/${searchAssistantId}`,
        params: {
          objectCode,
          sort,
        },
      }),
      transformResponse: (output: GETCriteriaType[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<GETCriteriaType[]>(output, meta),
    }),
    deleteCriteria: build.mutation<void, number>({
      query: id => ({
        url: URLS.FRONT_CFG + `/searchFilter/id/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Criterias'],
    }),
    fetchCriteriaById: build.mutation<GETCriteriaType, POSTCriteriaParams>({
      query: ({ code, searchAssistantId }) => ({
        url: URLS.FRONT_CFG + `/searchFilter/searchAssistant/id/${searchAssistantId}/code/${code}`,
      }),
    }),
    updateVisibilityById: build.mutation<GETCriteriaType, POSTVisibilityCriteriaType>({
      query: criteria => ({
        url: URLS.FRONT_CFG + '/searchFilter/visibility',
        method: 'POST',
        body: criteria,
      }),
      invalidatesTags: ['Criteria', 'Search', 'Criterias'],
    }),
    fetchAllCriteriaByObjectCodes: build.query<any, any>({
      query: ({ objectCodes, searchAssistantId }) => {
        const params = new URLSearchParams()
        objectCodes?.forEach((code: string) => params.append('objectCodes', code))

        return {
          url:
            URLS.FRONT_CFG +
            `/searchFilter/searchAssistant/${searchAssistantId}/filters/by-object-codes?${params.toString()}`,
          method: 'GET',
        }
      },
      transformResponse: (response: GETCriteriaType[], meta: FetchBaseQueryMeta) => {
        const model = response?.map(SearchAssistantCriteriasMapper.toModel) || []

        return transformResponseWithPagination<CriteriaType[]>(model, meta)
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const codes = queryArgs.objectCodes?.join(',') ?? ''

        return `${endpointName}:${codes}:${queryArgs.searchAssistantId}:${
          queryArgs.instanceId ?? ''
        }`
      },
      providesTags: ['Criterias'],
    }),
  }),
})

export const {
  useCreateOrUpdateCriteriaMutation,
  useFetchAllCriteriasQuery,
  useFetchAllCriteriaByObjectCodesQuery,
  useFetchCriteriasListQuery,
  useDeleteCriteriaMutation,
  useFetchCriteriaByIdMutation,
  useUpdateVisibilityByIdMutation,
  useAddDefaultCriteriaMutation,
  useFetchDefaultCriteriaQuery,
  useLazyFetchAllCriteriaByObjectCodesQuery,
} = CriteriaApi
